import { Box, Button, Card, CardContent, Chip, Grid, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useFetch } from '../../../hooks/useFecth';
import { useSearchTextInput } from '../../../hooks/useSearchTextInput';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';
import { getStoredUser } from '../../../services/authSession';
import { canCancelAdvertiserValidation } from '../utils/advertiserPermissions';

const statusOptions = [
    { label: 'Todos', value: 'ALL' },
    { label: 'Aguardando', value: 'WAITING' },
    { label: 'Validando', value: 'PROCESSING' },
    { label: 'Validado', value: 'VALIDATED' },
];

const RESCUE_STATUS_META = {
    WAITING: { label: 'Aguardando', color: 'default' },
    PROCESSING: { label: 'Validando', color: 'warning' },
    VALIDATED: { label: 'Validado', color: 'success' },
    FREE: { label: 'Livre', color: 'info' },
};

function getRescueStatusChipProps(status) {
    const key = String(status || 'WAITING')
        .trim()
        .toUpperCase();
    if (RESCUE_STATUS_META[key]) {
        return RESCUE_STATUS_META[key];
    }
    if (status) {
        return { label: String(status), color: 'default' };
    }
    return RESCUE_STATUS_META.WAITING;
}

function isRescueValidated(rescue) {
    return Boolean(rescue?.validatedAt) || String(rescue?.status || '').toUpperCase() === 'VALIDATED';
}

export default function AdvertiserRescues() {
    const [{ user }] = useCookies(['user']);
    const canCancel = canCancelAdvertiserValidation(user || getStoredUser());
    const { search, input, handleChange, handleReset } = useSearchTextInput();
    const { enqueueSnackbar, setErrors } = useErrors();
    const [status, setStatus] = useState('ALL');
    const [listPage, setListPage] = useState(0);
    const [rescues, setRescues] = useState([]);
    const [cancellingId, setCancellingId] = useState(null);
    const perPage = 15;

    const params = useMemo(
        () => ({
            params: {
                page: listPage,
                perPage,
                code: search || undefined,
                status: status === 'ALL' ? undefined : status,
            },
        }),
        [listPage, perPage, search, status],
    );

    const { data: pagePayload, isLoading, mutate } = useFetch('/advertiser/redemptions', params);

    useEffect(() => {
        setListPage(0);
        setRescues([]);
    }, [search, status]);

    useEffect(() => {
        if (!pagePayload?.data) return;
        const rows = pagePayload.data;
        if (listPage === 0) {
            setRescues(rows);
        } else {
            setRescues(prev => [...prev, ...rows]);
        }
    }, [pagePayload, listPage]);

    const total = pagePayload?.total ?? 0;
    const hasMore = Boolean(pagePayload?.hasMore);

    const totalValidated = pagePayload?.totalValidated ?? 0;
    const totalPending = pagePayload?.totalPending ?? 0;
    const totalAmountValidated = pagePayload?.totalAmountValidated ?? 0;
    const totalSaleValue = pagePayload?.totalSaleValue ?? 0;

    const loadMore = () => {
        if (hasMore && !isLoading) {
            setListPage(p => p + 1);
        }
    };

    const handleCancelValidation = async rescue => {
        if (!window.confirm(`Desfazer a validação do código ${rescue.code}? Ele voltará a aguardar validação.`)) {
            return;
        }
        try {
            setCancellingId(rescue.id);
            await server.post(`/advertiser/redemptions/${rescue.id}/cancel`);
            enqueueSnackbar('Validação desfeita.', { variant: 'success' });
            setListPage(0);
            setRescues([]);
            await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setCancellingId(null);
        }
    };

    return (
        <Stack spacing={3}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
            >
                <Typography variant="h4" fontWeight="bold">
                    Resgates
                </Typography>
                <Button
                    variant="contained"
                    component={RouterLink}
                    to="/anunciante/validar"
                    sx={{ textTransform: 'none', alignSelf: { xs: 'stretch', sm: 'center' } }}
                >
                    Validar código
                </Button>
            </Stack>
            <Grid container spacing={2} alignItems="stretch">
                {[
                    { label: 'Resgates cadastrados', value: total || '--' },
                    { label: 'Resgates validados', value: totalValidated || '--' },
                    { label: 'Resgates pendentes', value: totalPending || '--' },
                    {
                        label: 'Total em descontos',
                        value: totalAmountValidated
                            ? Number(totalAmountValidated).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                            : '--',
                    },
                    {
                        label: 'Total em vendas',
                        value: totalSaleValue
                            ? Number(totalSaleValue).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                            : '--',
                    },
                    { label: 'Último código validado', value: rescues?.[0]?.code || '--' },
                ].map(metric => (
                    <Grid item xs={12} sm={6} md={4} key={metric.label} sx={{ display: 'flex' }}>
                        <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardContent
                                sx={{
                                    flex: 1,
                                    py: 2,
                                    px: 2.5,
                                    '&:last-child': { pb: 2 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.3, minHeight: 36, display: 'block' }}
                                >
                                    {metric.label}
                                </Typography>
                                <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    sx={{
                                        mt: 'auto',
                                        lineHeight: 1.25,
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {metric.value}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Card>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Buscar por código"
                                value={input}
                                onChange={event => handleChange(event.target.value)}
                                InputProps={{
                                    endAdornment: input ? (
                                        <Button size="small" onClick={handleReset}>
                                            Limpar
                                        </Button>
                                    ) : null,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField select fullWidth label="Status" value={status} onChange={event => setStatus(event.target.value)}>
                                {statusOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Código</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Valor da venda</TableCell>
                                <TableCell>Desconto</TableCell>
                                <TableCell>Validador</TableCell>
                                <TableCell>Data</TableCell>
                                {canCancel ? <TableCell align="right">Ações</TableCell> : null}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rescues?.map(rescue => {
                                const statusChip = getRescueStatusChipProps(rescue.status);
                                const validated = isRescueValidated(rescue);
                                const saleValue = rescue.saleValue != null ? Number(rescue.saleValue) : null;
                                const discount =
                                    rescue.subscriberVoucherValue != null
                                        ? Number(rescue.subscriberVoucherValue)
                                        : saleValue != null && rescue.validatedAmount != null
                                          ? saleValue - Number(rescue.validatedAmount)
                                          : null;
                                return (
                                    <TableRow key={rescue.id}>
                                        <TableCell>
                                            <Typography fontWeight="bold">{rescue.code}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={statusChip.label} color={statusChip.color} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            {saleValue != null
                                                ? saleValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                                : '--'}
                                        </TableCell>
                                        <TableCell>
                                            {discount != null
                                                ? discount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                                : '--'}
                                        </TableCell>
                                        <TableCell>
                                            {rescue.ValidatedBy?.userName || (rescue.validatedByUserId != null ? `#${rescue.validatedByUserId}` : '--')}
                                        </TableCell>
                                        <TableCell>
                                            {rescue.validatedAt
                                                ? new Date(rescue.validatedAt).toLocaleString('pt-BR')
                                                : rescue.createdAt
                                                    ? new Date(rescue.createdAt).toLocaleString('pt-BR')
                                                    : '--'}
                                        </TableCell>
                                        {canCancel ? (
                                            <TableCell align="right">
                                                {validated ? (
                                                    <Button
                                                        size="small"
                                                        color="warning"
                                                        disabled={cancellingId === rescue.id}
                                                        onClick={() => handleCancelValidation(rescue)}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {cancellingId === rescue.id ? 'Desfazendo…' : 'Desfazer validação'}
                                                    </Button>
                                                ) : (
                                                    '—'
                                                )}
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    {rescues?.length === 0 && !isLoading && (
                        <Typography variant="body2" color="text.secondary" mt={2}>
                            Nenhum resgate encontrado.
                        </Typography>
                    )}
                    {hasMore && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Button variant="outlined" onClick={loadMore}>
                                Carregar mais
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Stack>
    );
}
